import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Image,
  FileText,
  FileImage,
  File,
  Download,
  ExternalLink,
  Paperclip,
  Upload,
  Loader2,
  Trash2,
  Send,
} from "lucide-react";
import { format } from "date-fns";
import { fi } from "date-fns/locale";
import {
  PROOF_STATUS_LABELS,
  PROOF_STATUS_COLORS,
  ProofStatus,
} from "@/config/order-constants";
import { OrderDetailData } from "@/types/orders";
import { toast } from "sonner";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { queryKeys } from "@/lib/query-keys";

interface OrderAssetsCardProps {
  orderProofs: OrderDetailData["order_proofs"];
  orderFiles: OrderDetailData["order_files"];
  orderId: string;
  orderNumber: string;
  customerName?: string;
  customerEmail?: string | null;
}

export function OrderAssetsCard({
  orderProofs,
  orderFiles,
  orderId,
  orderNumber,
  customerName,
  customerEmail,
}: OrderAssetsCardProps) {
  const queryClient = useQueryClient();
  const proofInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Normalize data - Supabase can return single object instead of array
  const proofs = Array.isArray(orderProofs) ? orderProofs : (orderProofs ? [orderProofs] : []);
  const files = Array.isArray(orderFiles) ? orderFiles : (orderFiles ? [orderFiles] : []);

  const [proofUrls, setProofUrls] = useState<Record<string, string>>({});
  const [loadingProofs, setLoadingProofs] = useState<Record<string, boolean>>({});
  const [uploadingProof, setUploadingProof] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [isDraggingProof, setIsDraggingProof] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [sendingProofId, setSendingProofId] = useState<string | null>(null);
  const [proofToDelete, setProofToDelete] = useState<{ id: string; version: number } | null>(null);
  const [fileToDelete, setFileToDelete] = useState<{ id: string; name: string } | null>(null);

  const getSignedUrl = async (
    bucketName: string,
    filePath: string
  ): Promise<string | null> => {
    const { data, error } = await supabase.storage
      .from(bucketName)
      .createSignedUrl(filePath, 3600);

    if (error) {
      console.error("Error getting signed URL:", error);
      return null;
    }
    return data.signedUrl;
  };

  // Load signed URLs for proofs on mount
  useEffect(() => {
    const loadProofUrls = async () => {
      const urls: Record<string, string> = {};
      for (const proof of proofs) {
        if (proof.file_path) {
          setLoadingProofs((prev) => ({ ...prev, [proof.id]: true }));
          const url = await getSignedUrl("order-proofs", proof.file_path);
          if (url) {
            urls[proof.id] = url;
          }
          setLoadingProofs((prev) => ({ ...prev, [proof.id]: false }));
        }
      }
      setProofUrls(urls);
    };
    if (proofs.length > 0) {
      loadProofUrls();
    }
  }, [proofs]);

  const handleOpenProof = async (proof: (typeof proofs)[0]) => {
    const url = proofUrls[proof.id];
    if (url) {
      window.open(url, "_blank");
    } else {
      const newUrl = await getSignedUrl("order-proofs", proof.file_path);
      if (newUrl) {
        window.open(newUrl, "_blank");
      } else {
        toast.error("Vedosta ei voitu avata");
      }
    }
  };

  const handleDownloadFile = async (file: (typeof files)[0]) => {
    const url = await getSignedUrl("order-attachments", file.file_path);
    if (url) {
      window.open(url, "_blank");
    } else {
      toast.error("Tiedostoa ei voitu ladata");
    }
  };

  // Upload proof mutation
  const uploadProofMutation = useMutation({
    mutationFn: async (file: File) => {
      // Get next version number
      const maxVersion = proofs.length > 0
        ? Math.max(...proofs.map(p => p.version || 1))
        : 0;
      const nextVersion = maxVersion + 1;

      // Create file path
      const fileExt = file.name.split(".").pop();
      const fileName = `vedos_v${nextVersion}.${fileExt}`;
      const filePath = `${orderId}/${fileName}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from("order-proofs")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Create database record
      const { error: dbError } = await supabase.from("order_proofs").insert({
        order_id: orderId,
        file_name: fileName,
        file_path: filePath,
        file_size: file.size,
        version: nextVersion,
        status: "pending",
      });

      if (dbError) throw dbError;
    },
    onSuccess: () => {
      toast.success("Vedos lisätty onnistuneesti");
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(orderId) });
    },
    onError: (error) => {
      console.error("Proof upload error:", error);
      toast.error("Vedoksen lisääminen epäonnistui");
    },
  });

  // Upload file mutation
  const uploadFileMutation = useMutation({
    mutationFn: async (file: File) => {
      // Create file path
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const filePath = `${orderId}/${timestamp}_${safeName}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from("order-attachments")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Create database record
      const { error: dbError } = await supabase.from("order_files").insert({
        order_id: orderId,
        file_name: file.name,
        file_path: filePath,
        file_size: file.size,
        file_type: file.type || "application/octet-stream",
      });

      if (dbError) throw dbError;
    },
    onSuccess: () => {
      toast.success("Tiedosto lisätty onnistuneesti");
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(orderId) });
    },
    onError: (error) => {
      console.error("File upload error:", error);
      toast.error("Tiedoston lisääminen epäonnistui");
    },
  });

  // Delete proof mutation (soft delete by changing status)
  const deleteProofMutation = useMutation({
    mutationFn: async (proofId: string) => {
      const { error } = await supabase
        .from("order_proofs")
        .delete()
        .eq("id", proofId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Vedos poistettu");
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(orderId) });
    },
    onError: (error) => {
      console.error("Delete proof error:", error);
      toast.error("Vedoksen poistaminen epäonnistui");
    },
  });

  // Delete file mutation
  const deleteFileMutation = useMutation({
    mutationFn: async (fileId: string) => {
      const { error } = await supabase
        .from("order_files")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", fileId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Tiedosto poistettu");
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(orderId) });
    },
    onError: (error) => {
      console.error("Delete file error:", error);
      toast.error("Tiedoston poistaminen epäonnistui");
    },
  });

  // Send proof to customer
  const sendProofMutation = useMutation({
    mutationFn: async (proofId: string) => {
      if (!customerEmail) {
        throw new Error("Asiakkaalla ei ole sähköpostiosoitetta");
      }

      const { error } = await supabase.functions.invoke("send-proof", {
        body: {
          proofId: proofId,
          orderId: orderId,
          customerEmail: customerEmail,
          customerName: customerName,
        },
      });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Vedos lähetetty asiakkaalle");
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(orderId) });
    },
    onError: (error: Error) => {
      console.error("Send proof error:", error);
      toast.error(error.message || "Vedoksen lähettäminen epäonnistui");
    },
  });

  // Handle proof file selection
  const handleProofSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles?.length) return;

    setUploadingProof(true);
    try {
      for (const file of Array.from(selectedFiles)) {
        await uploadProofMutation.mutateAsync(file);
      }
    } finally {
      setUploadingProof(false);
      if (proofInputRef.current) {
        proofInputRef.current.value = "";
      }
    }
  };

  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles?.length) return;

    setUploadingFile(true);
    try {
      for (const file of Array.from(selectedFiles)) {
        if (file.size > 20 * 1024 * 1024) {
          toast.error(`Tiedosto ${file.name} on liian suuri (max 20MB)`);
          continue;
        }
        await uploadFileMutation.mutateAsync(file);
      }
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Drag and drop handlers for proofs
  const handleProofDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingProof(true);
  };

  const handleProofDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingProof(false);
  };

  const handleProofDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingProof(false);

    const droppedFiles = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith("image/")
    );

    if (droppedFiles.length === 0) {
      toast.error("Vain kuvatiedostot sallittu vedoksille");
      return;
    }

    setUploadingProof(true);
    try {
      for (const file of droppedFiles) {
        await uploadProofMutation.mutateAsync(file);
      }
    } finally {
      setUploadingProof(false);
    }
  };

  // Drag and drop handlers for files
  const handleFileDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingFile(true);
  };

  const handleFileDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingFile(false);
  };

  const handleFileDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingFile(false);

    const droppedFiles = Array.from(e.dataTransfer.files);

    setUploadingFile(true);
    try {
      for (const file of droppedFiles) {
        if (file.size > 20 * 1024 * 1024) {
          toast.error(`Tiedosto ${file.name} on liian suuri (max 20MB)`);
          continue;
        }
        await uploadFileMutation.mutateAsync(file);
      }
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle send proof
  const handleSendProof = async (proofId: string) => {
    if (!customerEmail) {
      toast.error("Asiakkaalla ei ole sähköpostiosoitetta");
      return;
    }
    setSendingProofId(proofId);
    try {
      await sendProofMutation.mutateAsync(proofId);
    } finally {
      setSendingProofId(null);
    }
  };

  const getFileIcon = (fileType: string | null) => {
    if (!fileType) return <File className="h-5 w-5 text-muted-foreground" />;
    if (fileType.startsWith("image/"))
      return <FileImage className="h-5 w-5 text-blue-500" />;
    if (fileType.includes("pdf"))
      return <FileText className="h-5 w-5 text-red-500" />;
    return <File className="h-5 w-5 text-muted-foreground" />;
  };

  const getStatusColor = (status: string) => {
    return (
      PROOF_STATUS_COLORS[status as ProofStatus] ||
      "bg-muted text-muted-foreground"
    );
  };

  const getStatusLabel = (status: string) => {
    return PROOF_STATUS_LABELS[status as ProofStatus] || status;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Filter out deleted files
  const visibleFiles = files.filter((f) => !f.deleted_at);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Paperclip className="h-5 w-5" />
          Liitteet
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="proofs">
          <TabsList className="mb-4">
            <TabsTrigger value="proofs">
              Vedokset {proofs.length > 0 && `(${proofs.length})`}
            </TabsTrigger>
            <TabsTrigger value="files">
              Tiedostot {visibleFiles.length > 0 && `(${visibleFiles.length})`}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="proofs" className="space-y-4">
            {/* Upload area */}
            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-4 text-center transition-colors",
                isDraggingProof
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-muted-foreground/50"
              )}
              onDragOver={handleProofDragOver}
              onDragLeave={handleProofDragLeave}
              onDrop={handleProofDrop}
            >
              <input
                ref={proofInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleProofSelect}
                className="hidden"
              />
              {uploadingProof ? (
                <div className="flex items-center justify-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Ladataan...</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-6 w-6 mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Vedä kuva tähän tai{" "}
                    <button
                      type="button"
                      onClick={() => proofInputRef.current?.click()}
                      className="text-primary hover:underline"
                    >
                      valitse tiedosto
                    </button>
                  </p>
                </div>
              )}
            </div>

            {/* Proofs grid */}
            {proofs.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {proofs.map((proof) => (
                  <div
                    key={proof.id}
                    className="group relative border rounded-lg overflow-hidden"
                  >
                    {/* Thumbnail */}
                    <div
                      className="aspect-square bg-muted flex items-center justify-center relative cursor-pointer"
                      onClick={() => handleOpenProof(proof)}
                    >
                      {proofUrls[proof.id] ? (
                        <img
                          src={proofUrls[proof.id]}
                          alt={proof.file_name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            e.currentTarget.nextElementSibling?.classList.remove(
                              "hidden"
                            );
                          }}
                        />
                      ) : null}
                      <div
                        className={
                          proofUrls[proof.id]
                            ? "hidden absolute inset-0 flex items-center justify-center bg-muted"
                            : "absolute inset-0 flex items-center justify-center bg-muted"
                        }
                      >
                        {loadingProofs[proof.id] ? (
                          <Loader2 className="h-8 w-8 text-muted-foreground/50 animate-spin" />
                        ) : (
                          <Image className="h-8 w-8 text-muted-foreground/50" />
                        )}
                      </div>

                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <ExternalLink className="h-6 w-6 text-white" />
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-2 space-y-1">
                      <div className="flex items-center justify-between gap-1">
                        <Badge className={`text-xs ${getStatusColor(proof.status)}`}>
                          {getStatusLabel(proof.status)}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          v{proof.version}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(proof.created_at), "d.M.yyyy", { locale: fi })}
                      </p>
                    </div>

                    {/* Action buttons */}
                    <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {proof.status === "pending" && customerEmail && (
                        <Button
                          size="icon"
                          variant="secondary"
                          className="h-7 w-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendProof(proof.id);
                          }}
                          disabled={sendingProofId === proof.id}
                          title="Lähetä asiakkaalle"
                        >
                          {sendingProofId === proof.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Send className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      )}
                      {proof.status === "pending" && (
                        <Button
                          size="icon"
                          variant="destructive"
                          className="h-7 w-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            setProofToDelete({ id: proof.id, version: proof.version });
                          }}
                          title="Poista vedos"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground">
                <Image className="mx-auto h-10 w-10 text-muted-foreground/50 mb-2" />
                <p className="text-sm">Ei vedoksia</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="files" className="space-y-4">
            {/* Upload area */}
            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-4 text-center transition-colors",
                isDraggingFile
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-muted-foreground/50"
              )}
              onDragOver={handleFileDragOver}
              onDragLeave={handleFileDragLeave}
              onDrop={handleFileDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              {uploadingFile ? (
                <div className="flex items-center justify-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Ladataan...</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-6 w-6 mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Vedä tiedostot tähän tai{" "}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-primary hover:underline"
                    >
                      valitse tiedostot
                    </button>
                  </p>
                  <p className="text-xs text-muted-foreground">Max 20 MB / tiedosto</p>
                </div>
              )}
            </div>

            {/* Files list */}
            {visibleFiles.length > 0 ? (
              <div className="space-y-1">
                {visibleFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-muted/50 transition-colors group"
                  >
                    {getFileIcon(file.file_type)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{file.file_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(file.file_size)} •{" "}
                        {format(new Date(file.created_at), "d.M.yyyy", { locale: fi })}
                      </p>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleDownloadFile(file)}
                        title="Lataa tiedosto"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => setFileToDelete({ id: file.id, name: file.file_name })}
                        title="Poista tiedosto"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground">
                <FileText className="mx-auto h-10 w-10 text-muted-foreground/50 mb-2" />
                <p className="text-sm">Ei tiedostoja</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Delete proof confirmation dialog */}
      <AlertDialog open={!!proofToDelete} onOpenChange={(open) => !open && setProofToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Poista vedos?</AlertDialogTitle>
            <AlertDialogDescription>
              Haluatko varmasti poistaa vedoksen v{proofToDelete?.version}? Tätä toimintoa ei voi perua.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Peruuta</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (proofToDelete) {
                  deleteProofMutation.mutate(proofToDelete.id);
                  setProofToDelete(null);
                }
              }}
            >
              Poista
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete file confirmation dialog */}
      <AlertDialog open={!!fileToDelete} onOpenChange={(open) => !open && setFileToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Poista tiedosto?</AlertDialogTitle>
            <AlertDialogDescription>
              Haluatko varmasti poistaa tiedoston "{fileToDelete?.name}"? Tätä toimintoa ei voi perua.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Peruuta</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (fileToDelete) {
                  deleteFileMutation.mutate(fileToDelete.id);
                  setFileToDelete(null);
                }
              }}
            >
              Poista
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
